package top.wcpe.allincore.dsl.compiler.semantic

import top.wcpe.allincore.dsl.compiler.ast.ExprAst
import top.wcpe.allincore.dsl.compiler.ast.ScreenAst
import top.wcpe.allincore.dsl.compiler.ast.StmtAst
import top.wcpe.allincore.dsl.compiler.diag.DiagnosticCollector
import top.wcpe.allincore.dsl.compiler.diag.DslError

/**
 * await 位置语义检查：当前 lowerer 仅支持两种顶层语句形态承载 await：
 *   - `var x = await net.request(...)`
 *   - 裸 `await net.request(...)`（[StmtAst.ExprStmt] 包裹）
 *
 * 其它出现位置（return / 赋值 rhs / 调用实参 / BinaryOp / 字符串模板插值 等）均报
 * [DslError.E605_AWAIT_IN_EXPR_POSITION]。在语义阶段尽早发现，避免落到 codegen
 * 抛 IllegalStateException 形式的内部错误，提升用户诊断体验。
 *
 * try/catch/finally 体内 await 由 parser 阶段单独硬拒（[DslError.E601_AWAIT_IN_TRY]）。
 */
class AwaitPositionChecker(private val diag: DiagnosticCollector) {

    fun check(screen: ScreenAst) {
        screen.funs.forEach { checkBody(it.body) }
        // root 元素树中的事件入口表达式不会含 await（解析层已拒绝），无需在此遍历
    }

    private fun checkBody(stmts: List<StmtAst>) {
        for (s in stmts) checkStmt(s)
    }

    private fun checkStmt(s: StmtAst) {
        when (s) {
            // 顶层位置允许：裸 await
            is StmtAst.ExprStmt -> {
                if (s.call is ExprAst.AwaitExpr) return
                walkExprForbidAwait(s.call, "ExprStmt(call)")
            }
            // 顶层位置允许：var x = await ...
            is StmtAst.VarDeclStmt -> {
                if (s.initializer is ExprAst.AwaitExpr) return
                walkExprForbidAwait(s.initializer, "var ${s.name} = ...")
            }
            is StmtAst.AssignStmt -> walkExprForbidAwait(s.rhs, "赋值 rhs")
            is StmtAst.IfStmt -> {
                walkExprForbidAwait(s.cond, "if 条件")
                checkBody(s.thenBody)
                checkBody(s.elseBody)
            }
            is StmtAst.WhileStmt -> {
                walkExprForbidAwait(s.cond, "while 条件")
                checkBody(s.body)
            }
            is StmtAst.ForInStmt -> {
                walkExprForbidAwait(s.iterable, "for-in 迭代源")
                checkBody(s.body)
            }
            is StmtAst.WhenStmt -> walkExprForbidAwait(s.whenExpr, "when 表达式")
            is StmtAst.ReturnStmt -> s.value?.let { walkExprForbidAwait(it, "return 值") }
            is StmtAst.TryStmt -> {
                checkBody(s.tryBody)
                checkBody(s.catchBody)
                checkBody(s.finallyBody)
            }
            // batch 语句：递归检查 body（与 TryStmt 的 tryBody 处理一致）
            is StmtAst.BatchStmt -> checkBody(s.body)
            is StmtAst.FunDeclStmt -> checkBody(s.decl.body)
            is StmtAst.BreakStmt, is StmtAst.ContinueStmt -> Unit
        }
    }

    /** 在表达式中递归找 AwaitExpr，命中即报 E605。 */
    private fun walkExprForbidAwait(e: ExprAst, contextHint: String) {
        when (e) {
            is ExprAst.AwaitExpr ->
                diag.error(DslError.E605_AWAIT_IN_EXPR_POSITION, e.position, contextHint)
            is ExprAst.BinaryOp -> {
                walkExprForbidAwait(e.left, contextHint); walkExprForbidAwait(e.right, contextHint)
            }
            is ExprAst.UnaryOp -> walkExprForbidAwait(e.operand, contextHint)
            is ExprAst.MemberAccess -> walkExprForbidAwait(e.target, contextHint)
            is ExprAst.SafeMemberAccess -> walkExprForbidAwait(e.target, contextHint)
            // M10 Stage 6：`target!!` 透传 target
            is ExprAst.NonNullAssert -> walkExprForbidAwait(e.target, contextHint)
            is ExprAst.MemberCall -> {
                walkExprForbidAwait(e.target, contextHint)
                e.args.forEach { walkExprForbidAwait(it, contextHint) }
                e.namedArgs.forEach { walkExprForbidAwait(it.value, contextHint) }
            }
            is ExprAst.SafeMemberCall -> {
                walkExprForbidAwait(e.target, contextHint)
                e.args.forEach { walkExprForbidAwait(it, contextHint) }
            }
            is ExprAst.Elvis -> {
                walkExprForbidAwait(e.left, contextHint); walkExprForbidAwait(e.right, contextHint)
            }
            is ExprAst.Ternary -> {
                walkExprForbidAwait(e.cond, contextHint)
                walkExprForbidAwait(e.thenExpr, contextHint)
                walkExprForbidAwait(e.elseExpr, contextHint)
            }
            is ExprAst.IfExpr -> {
                walkExprForbidAwait(e.cond, contextHint)
                checkBody(e.thenBody); checkBody(e.elseBody)
            }
            is ExprAst.Interp -> e.parts.forEach {
                if (it is ExprAst.Interp.InterpPart.ExprPart) walkExprForbidAwait(it.expr, contextHint)
            }
            is ExprAst.ListLiteralExpr -> e.elements.forEach { walkExprForbidAwait(it, contextHint) }
            is ExprAst.MapLiteralExpr -> e.entries.forEach { walkExprForbidAwait(it.value, contextHint) }
            is ExprAst.RangeExpr -> {
                walkExprForbidAwait(e.start, contextHint); walkExprForbidAwait(e.endValue, contextHint)
            }
            is ExprAst.IndexExpr -> {
                walkExprForbidAwait(e.target, contextHint); walkExprForbidAwait(e.index, contextHint)
            }
            is ExprAst.CallExpr -> {
                walkExprForbidAwait(e.callee, contextHint)
                e.args.forEach { walkExprForbidAwait(it, contextHint) }
            }
            is ExprAst.BuiltinCallExpr -> e.args.forEach { walkExprForbidAwait(it, contextHint) }
            is ExprAst.NetSubscribeCall -> walkExprForbidAwait(e.handler, contextHint)
            is ExprAst.NetRequestCall -> {
                e.payload.forEach { walkExprForbidAwait(it.value, contextHint) }
                e.callback?.let { walkExprForbidAwait(it, contextHint) }
                e.timeoutMs?.let { walkExprForbidAwait(it, contextHint) }
            }
            is ExprAst.LambdaExpr -> {
                // M4 A2 Stage 1：
                // - 允许 lambda body 内顶层位置出现 await（ExprStmt(AwaitExpr) / VarDeclStmt(initializer=AwaitExpr)），
                //   suspend lambda 的异步返回值来自 body 末位 ExprStmt
                // - 末位若不是 ExprStmt（如 IfStmt / WhileStmt / VarDeclStmt 等）→ E608
                // - body 其它位置的 await（嵌入表达式）仍走 walkExprForbidAwait → E605
                if (e.isSuspend && e.body.isNotEmpty()) {
                    val tail = e.body.last()
                    if (tail !is StmtAst.ExprStmt) {
                        diag.error(
                            DslError.E608_SUSPEND_LAMBDA_PARTIAL_RETURN,
                            tail.position,
                            tail::class.simpleName ?: "?",
                        )
                    }
                }
                checkBody(e.body)
            }
            is ExprAst.WhenExpr -> {
                walkExprForbidAwait(e.subject, contextHint)
                e.branches.forEach { br ->
                    br.values.forEach { walkExprForbidAwait(it, contextHint) }
                    checkBody(br.body)
                }
                e.elseBody?.let { checkBody(it) }
            }
            // 叶子节点
            is ExprAst.Literal, is ExprAst.NullLit, is ExprAst.IdentRef,
            is ExprAst.LocalRef, is ExprAst.StateRef, is ExprAst.SessionRef, is ExprAst.PersistRef, is ExprAst.DerivedRef, is ExprAst.PropsRef,
            is ExprAst.ThemeRef, is ExprAst.FunRefExpr, is ExprAst.IsExpr -> Unit
        }
    }
}
